/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IHeroBannerSlideViewProps } from './hero-banner-slide';
import { SwiperSlide } from 'swiper/react';

export default (props: IHeroBannerSlideViewProps) => {
    const { config } = props;
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const backgroundImageDesktop = config.backgroundImageDesktop;
    const backgroundImageMobile = config.backgroundImageMobile;
    const videoDesktop = config.videoDesktop;
    const videoMobile = config.videoMobile;
    const useSameVideoOnMobile = config.useSameVideoOnMobile || false;
    const contentBackgroundOpacity = config.contentBackgroundOpacity !== undefined ? config.contentBackgroundOpacity / 100 : 0;

    // Helper function to extract video URL from IVideoData
    const getVideoUrl = (video: any) => {
        if (!video) {
            return null;
        }
        if (typeof video === 'string') {
            return video;
        }
        // @ts-ignore
        return video._links?.binaryReferences?.[0]?.clientHref || null;
    };

    // Determine which media to show based on priority:
    // 1. Video has priority over image
    // 2. Mobile-specific media first, then fallback
    // 3. If useSameVideoOnMobile is true and no mobile video, use desktop video on mobile

    let mediaToShow: 'video' | 'image' | null = null;
    let videoUrl: string | null = null;
    let backgroundImage: any = null;

    if (isMobile) {
        // Mobile logic
        const mobileVideoUrl = getVideoUrl(videoMobile);
        const desktopVideoUrl = getVideoUrl(videoDesktop);

        if (mobileVideoUrl) {
            // Priority 1: Mobile video exists
            mediaToShow = 'video';
            videoUrl = mobileVideoUrl;
        } else if (useSameVideoOnMobile && desktopVideoUrl) {
            // Priority 2: Use desktop video on mobile if flag is enabled
            mediaToShow = 'video';
            videoUrl = desktopVideoUrl;
        } else if (backgroundImageMobile) {
            // Priority 3: Mobile image
            mediaToShow = 'image';
            backgroundImage = backgroundImageMobile;
        } else if (backgroundImageDesktop) {
            // Priority 4: Fallback to desktop image
            mediaToShow = 'image';
            backgroundImage = backgroundImageDesktop;
        }
    } else {
        // Desktop logic
        const desktopVideoUrl = getVideoUrl(videoDesktop);

        if (desktopVideoUrl) {
            // Priority 1: Desktop video exists
            mediaToShow = 'video';
            videoUrl = desktopVideoUrl;
        } else if (backgroundImageDesktop) {
            // Priority 2: Desktop image
            mediaToShow = 'image';
            backgroundImage = backgroundImageDesktop;
        }
    }

    const slideStyle =
        mediaToShow === 'image' && backgroundImage
            ? {
                  backgroundImage: `url(${backgroundImage.src || backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat'
              }
            : {};

    const colorMap: { [key: string]: string } = {
        copperOrange: '#DC4E0F',
        warmBeige: '#D6D2C4',
        darkBrown: '#4B1800',
        cream: '#F7EFDD',
        yellow: '#f9c22e',
        white: '#FFFFFF',
        black: '#000000'
    };

    const titleStyle = config.titleColor ? { color: colorMap[config.titleColor] || config.titleColor } : {};

    const labelColor = config.labelColor ? colorMap[config.labelColor] || config.labelColor : colorMap.yellow;

    const labelStyle = {
        color: labelColor,
        borderColor: labelColor,
        backgroundColor: 'transparent',
        border: `solid 1px ${labelColor}`
    };

    const slideTitle = config.title || config.label || 'Slide';
    const contentPosition = config?.contentPosition || 'left';
    const paginationTheme = config.paginationTheme || 'light';

    const buttonCustomProps = {
        '--button-bg-color': config.buttonBackgroundColor
            ? colorMap[config.buttonBackgroundColor] || config.buttonBackgroundColor
            : colorMap.white,
        '--button-text-color': config.buttonTextColor ? colorMap[config.buttonTextColor] || config.buttonTextColor : colorMap.darkBrown
    } as React.CSSProperties;

    const buttonHoverClass = config.buttonHoverStyle === 'light' ? 'btn-light-hover' : 'btn-dark-hover';

    return (
        <SwiperSlide style={slideStyle} aria-label={slideTitle} data-pagination-theme={paginationTheme}>
            {mediaToShow === 'video' && videoUrl && (
                <video className='slide-video-background' autoPlay loop muted playsInline aria-hidden='true'>
                    <source src={videoUrl} type='video/mp4' />
                </video>
            )}
            {contentBackgroundOpacity > 0 && (
                <div
                    className='slide-overlay'
                    style={{
                        backgroundColor: `rgba(0, 0, 0, ${contentBackgroundOpacity})`
                    }}
                />
            )}
            <div className='container slide-container'>
                <div className={`slide-content content-${contentPosition}`}>
                    {config.label && (
                        <div className='slide-label' style={labelStyle}>
                            {config.label}
                        </div>
                    )}
                    {config.title && (
                        <h1 className='slide-title' style={titleStyle}>
                            {config.title}
                        </h1>
                    )}
                    {config.subtitle && <div dangerouslySetInnerHTML={{ __html: config.subtitle as string }} className='slide-subtitle' />}
                    <div className='slide-buttons-container'>
                        {config.buttonUrl && config.buttonText && (
                            <a
                                href={config.buttonUrl?.destinationUrl || '#'}
                                className={`slide-button ${buttonHoverClass}`}
                                style={buttonCustomProps}
                                aria-label={`${config.buttonText} - ${slideTitle}`}
                            >
                                {config.buttonText}
                            </a>
                        )}
                        {config.secondaryButtonText && config.secondaryButtonUrl && (
                            <a
                                href={config.secondaryButtonUrl?.destinationUrl || '#'}
                                className='secondary-cta'
                                aria-label={`${config.secondaryButtonText} - ${slideTitle}`}
                            >
                                {config.secondaryButtonText}
                            </a>
                        )}
                    </div>
                    {config.blockText && (
                        <div dangerouslySetInnerHTML={{ __html: config.blockText as string }} className='slide-block-text' />
                    )}
                </div>
            </div>
        </SwiperSlide>
    );
};
